import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Cloud, CloudRain, Wind, Zap, AlertTriangle, ArrowUpRight, Sun, CloudLightning, Snowflake } from "lucide-react";
import { cn } from "@/lib/utils";

type DayForecast = {
  date: string;
  day: string;
  icon: typeof Sun;
  hi: number;
  lo: number;
  pop: number; // % rain
  hail: boolean;
  wind: number;
  label: string;
};

function pickIcon(condition: string) {
  const c = (condition || "").toLowerCase();
  if (c.includes("hail") || c.includes("thunder")) return CloudLightning;
  if (c.includes("rain") || c.includes("shower")) return CloudRain;
  if (c.includes("snow")) return Snowflake;
  if (c.includes("cloud")) return Cloud;
  return Sun;
}

export function MobileStormPulse() {
  const navigate = useNavigate();
  const [days, setDays] = useState<DayForecast[]>([]);
  const [alert, setAlert] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [pulse, setPulse] = useState(0);

  // Animated "storm radar" pulse
  useEffect(() => {
    const t = setInterval(() => setPulse((p) => (p + 1) % 100), 60);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await supabase.functions.invoke("get-weather-forecast", {
          body: { location: "auto", days: 5 },
        });

        const forecast = data?.forecast || data?.days || [];
        const mapped: DayForecast[] = forecast.slice(0, 5).map((d: any, i: number) => {
          const dayDate = d.date ? new Date(d.date) : new Date(Date.now() + i * 86400000);
          const condition = d.condition || d.summary || "";
          return {
            date: dayDate.toISOString(),
            day: i === 0 ? "Today" : dayDate.toLocaleDateString("en-US", { weekday: "short" }),
            icon: pickIcon(condition),
            hi: Math.round(d.high ?? d.maxTemp ?? 72),
            lo: Math.round(d.low ?? d.minTemp ?? 55),
            pop: Math.round(d.precipitation_chance ?? d.pop ?? 0),
            hail: !!(d.hail_risk || /hail/i.test(condition)),
            wind: Math.round(d.wind_speed ?? d.wind ?? 8),
            label: condition,
          };
        });

        if (mapped.length === 0) {
          // Graceful demo data so the widget never looks broken
          const demoConditions = ["Sunny", "Partly Cloudy", "Thunderstorms", "Showers", "Clear"];
          for (let i = 0; i < 5; i++) {
            mapped.push({
              date: new Date(Date.now() + i * 86400000).toISOString(),
              day: i === 0 ? "Today" : new Date(Date.now() + i * 86400000).toLocaleDateString("en-US", { weekday: "short" }),
              icon: pickIcon(demoConditions[i]),
              hi: 72 + i,
              lo: 55 + i,
              pop: i === 2 ? 80 : i === 3 ? 40 : 10,
              hail: i === 2,
              wind: 6 + i * 2,
              label: demoConditions[i],
            });
          }
        }

        setDays(mapped);
        const hailDay = mapped.find((d) => d.hail);
        if (hailDay) setAlert(`Hail risk ${hailDay.day} — prime canvassing window after.`);
        else if (mapped[0]?.pop > 60) setAlert("Wet day — push phone follow-ups, not knocks.");
        else setAlert(null);
      } catch (e) {
        console.error("storm pulse error:", e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="rounded-2xl border border-border/60 bg-card/60 backdrop-blur-xl p-5 h-44 animate-pulse" />
    );
  }

  const today = days[0];
  const ToIcon = today?.icon || Sun;

  return (
    <div className="relative overflow-hidden rounded-2xl border border-border/60 bg-gradient-to-br from-card via-card to-background">
      {/* Animated radar pulse background */}
      <div className="pointer-events-none absolute -right-12 -top-12 h-48 w-48">
        <div
          className="absolute inset-0 rounded-full border-2 border-primary/20"
          style={{ transform: `scale(${0.4 + (pulse % 50) / 50})`, opacity: 1 - (pulse % 50) / 50 }}
        />
        <div
          className="absolute inset-0 rounded-full border-2 border-primary/30"
          style={{ transform: `scale(${0.4 + ((pulse + 25) % 50) / 50})`, opacity: 1 - ((pulse + 25) % 50) / 50 }}
        />
      </div>

      <div className="relative p-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="relative">
              <Zap className="h-4 w-4 text-primary" strokeWidth={2.5} />
              <span className="absolute -top-0.5 -right-0.5 h-1.5 w-1.5 rounded-full bg-primary animate-ping" />
            </div>
            <h3 className="text-sm font-bold tracking-tight">Storm Pulse</h3>
          </div>
          <button
            onClick={() => navigate("/storm-watch")}
            className="flex items-center gap-1 text-[11px] font-medium text-primary"
          >
            Live radar <ArrowUpRight className="h-3 w-3" />
          </button>
        </div>

        {/* Today's headline */}
        <div className="flex items-end justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-2xl bg-primary/15 flex items-center justify-center">
              <ToIcon className="h-6 w-6 text-primary" strokeWidth={2.25} />
            </div>
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Today · {today?.label || "Clear"}
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-bold tracking-tight tabular-nums">{today?.hi ?? 72}°</span>
                <span className="text-sm text-muted-foreground tabular-nums">/ {today?.lo ?? 55}°</span>
              </div>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1 text-[11px]">
            <span className="flex items-center gap-1 text-muted-foreground">
              <Wind className="h-3 w-3" /> {today?.wind ?? 8} mph
            </span>
            <span className="flex items-center gap-1 text-muted-foreground">
              <CloudRain className="h-3 w-3" /> {today?.pop ?? 0}%
            </span>
          </div>
        </div>

        {/* 5-day strip */}
        <div className="grid grid-cols-5 gap-1.5 mb-3">
          {days.map((d, i) => {
            const Ic = d.icon;
            const isHot = d.hail || d.pop > 60;
            return (
              <button
                key={d.date}
                onClick={() => navigate("/storm-watch")}
                className={cn(
                  "rounded-xl py-2 px-1 flex flex-col items-center gap-1 transition-all active:scale-95 border",
                  isHot
                    ? "bg-primary/10 border-primary/40 shadow-[0_0_12px_-4px_hsl(var(--primary)/0.5)]"
                    : "bg-muted/30 border-border/30 hover:bg-muted/50",
                  i === 0 && "ring-1 ring-primary/30"
                )}
              >
                <span className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">
                  {d.day}
                </span>
                <Ic className={cn("h-4 w-4", isHot ? "text-primary" : "text-foreground/70")} />
                <span className="text-[11px] font-bold tabular-nums">{d.hi}°</span>
                {d.hail && (
                  <span className="text-[8px] font-bold text-primary uppercase">Hail</span>
                )}
              </button>
            );
          })}
        </div>

        {/* Smart insight */}
        {alert && (
          <div className="flex items-start gap-2 rounded-xl bg-primary/10 border border-primary/30 px-3 py-2">
            <AlertTriangle className="h-3.5 w-3.5 text-primary flex-shrink-0 mt-0.5" />
            <p className="text-[11px] font-medium leading-snug">{alert}</p>
          </div>
        )}
      </div>
    </div>
  );
}
